/**
 * 🚀 UNIVERSAL DIVIDER ELEMENT 2025
 * 
 * Dynamisches Divider-Element für HeroHeader
 * - Verschiedene Divider-Styles (simpel bis edel)
 * - Floating Toolbar für Preset-Auswahl
 * - Länge einstellbar (100% oder Pixel)
 * - Farbe anpassbar
 * - Voll responsive (Desktop/Mobile)
 */

import React, { useState, useRef } from 'react';
import DividerFloatingToolbar from './DividerFloatingToolbar';
import { useDebouncedCallback } from '../../hooks/useDebounce';
import './UniversalDividerElement.css';

function appendPx(value) {
    if (typeof value === 'string' && isNaN(value)) {
        return value;
    }
    return `${value}px`;
}

function UniversalDividerElement({
    element,
    isEditMode,
    isMobileLayout,
    onUpdate,
    onSelect,
    colors = {},
    isCurrentlyEditing = false,
    onEditingStart = () => {},
    onEditingEnd = () => {}
}) {
    const [showToolbar, setShowToolbar] = useState(false);
    const dividerRef = useRef(null);

    // ⚡ OPTIMISTIC UI: Lokaler State für Slider-Werte (SOFORTIGES Feedback)
    const [localStyles, setLocalStyles] = useState({
        thickness: element.dividerThickness,
        width: element.dividerWidth,
        marginTop: element.marginTop,
        marginTopMobile: element.marginTopMobile,
        marginBottom: element.marginBottom,
        marginBottomMobile: element.marginBottomMobile
    });

    // ⚡ Debounced Parent Update (300ms für Firebase)
    const debouncedParentUpdate = useDebouncedCallback((updates) => {
        if (onUpdate) {
            onUpdate(updates);
        }
    }, 300);

    // ✅ Divider Style Presets
    const getDividerStyles = () => {
        const style = element.dividerStyle || 'simple-line';
        const color = element.dividerColor || colors.accentColor || '#5C80BC';
        // ⚡ OPTIMISTIC: Use local state OR props
        const thickness = localStyles.thickness ?? element.dividerThickness ?? 2;
        const width = localStyles.width ?? element.dividerWidth ?? 100;
        const widthType = element.dividerWidthType || 'percent'; // 'percent' or 'pixel'
        const maxWidth = element.dividerMaxWidth || 800;
        
        const baseStyles = {
            width: widthType === 'percent' ? `${width}%` : appendPx(width),
            maxWidth: appendPx(maxWidth),
            margin: '0 auto', // ✅ Zentriert
            transition: 'all 0.3s ease'
        };

        const styleMap = {
            'simple-line': {
                ...baseStyles,
                height: appendPx(thickness),
                background: color,
                border: 'none',
                borderRadius: appendPx(thickness / 2)
            },
            'gradient-line': {
                ...baseStyles,
                height: appendPx(thickness),
                background: `linear-gradient(90deg, transparent 0%, ${color} 50%, transparent 100%)`,
                border: 'none',
                borderRadius: appendPx(thickness / 2)
            },
            'dashed-line': {
                ...baseStyles,
                height: appendPx(thickness),
                background: 'transparent',
                border: 'none',
                borderTop: `${thickness}px dashed ${color}`,
                borderRadius: appendPx(thickness / 2)
            },
            'dotted-line': {
                ...baseStyles,
                height: appendPx(thickness),
                background: 'transparent',
                border: 'none',
                borderTop: `${thickness}px dotted ${color}`,
                borderRadius: appendPx(thickness / 2)
            },
            'double-line': {
                ...baseStyles,
                height: appendPx(Math.max(thickness * 2, 6)),
                position: 'relative',
                background: 'transparent'
            },
            'thick-gradient': {
                ...baseStyles,
                height: appendPx(Math.max(thickness, 3)),
                background: `linear-gradient(135deg, ${color} 0%, ${color}cc 50%, ${color} 100%)`,
                border: 'none',
                borderRadius: appendPx(thickness / 2),
                boxShadow: `0 2px 6px ${color}30`
            },
            'elegant-shadow': {
                ...baseStyles,
                height: appendPx(thickness),
                background: color,
                border: 'none',
                borderRadius: appendPx(thickness / 2),
                boxShadow: `0 2px 8px ${color}40`
            },
            'glow-line': {
                ...baseStyles,
                height: appendPx(thickness),
                background: color,
                border: 'none',
                borderRadius: appendPx(thickness / 2),
                boxShadow: `0 0 12px ${color}80, 0 0 4px ${color}60`
            },
            'premium-fade': {
                ...baseStyles,
                height: appendPx(thickness),
                background: `linear-gradient(90deg, 
                    ${color}00 0%, 
                    ${color}40 20%, 
                    ${color} 50%, 
                    ${color}40 80%, 
                    ${color}00 100%)`,
                border: 'none',
                borderRadius: appendPx(thickness / 2)
            }
        };

        return styleMap[style] || styleMap['simple-line'];
    };

    const handleClick = (e) => {
        if (!isEditMode) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        if (!isCurrentlyEditing) {
            onEditingStart();
            setShowToolbar(true);
            if (onSelect) onSelect();
        }
    };

    const handleToolbarClose = () => {
        setShowToolbar(false);
        onEditingEnd();
    };

    const handleStyleChange = (updates) => {
        // Slider-Keys die optimistisch sein sollen
        const sliderKeys = [
            'dividerThickness', 
            'dividerWidth',
            'marginTop', 'marginTopMobile',
            'marginBottom', 'marginBottomMobile'
        ];
        
        const updatedKeys = Object.keys(updates);
        const hasSliderValue = updatedKeys.some(key => sliderKeys.includes(key));
        
        if (hasSliderValue) {
            // ✅ SLIDER VALUES: Optimistic Local State + Debounced Parent
            const localUpdates = {};
            if (updates.dividerThickness !== undefined) localUpdates.thickness = updates.dividerThickness;
            if (updates.dividerWidth !== undefined) localUpdates.width = updates.dividerWidth;
            if (updates.marginTop !== undefined) localUpdates.marginTop = updates.marginTop;
            if (updates.marginTopMobile !== undefined) localUpdates.marginTopMobile = updates.marginTopMobile;
            if (updates.marginBottom !== undefined) localUpdates.marginBottom = updates.marginBottom;
            if (updates.marginBottomMobile !== undefined) localUpdates.marginBottomMobile = updates.marginBottomMobile;
            
            setLocalStyles(prev => ({ ...prev, ...localUpdates }));
            
            // Debounced Parent Update
            debouncedParentUpdate({ ...element, ...updates });
        } else {
            // ✅ NON-SLIDER VALUES (Colors, Presets): Sofort speichern
            if (onUpdate) {
                onUpdate({ ...element, ...updates });
            }
        }
    };

    const dividerStyles = getDividerStyles();

    return (
        <div 
            ref={dividerRef}
            className={`universal-divider-container ${isEditMode ? 'edit-mode' : ''}`}
            style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                // ✅ MINDESTHÖHE für bessere Sichtbarkeit und Margins (auch im Live Mode)
                minHeight: '40px',
                // ⚡ OPTIMISTIC: Use local state OR props
                marginTop: appendPx(isMobileLayout ? 
                    (localStyles.marginTopMobile ?? element.marginTopMobile ?? localStyles.marginTop ?? element.marginTop ?? 20) : 
                    (localStyles.marginTop ?? element.marginTop ?? 20)
                ),
                marginBottom: appendPx(isMobileLayout ? 
                    (localStyles.marginBottomMobile ?? element.marginBottomMobile ?? localStyles.marginBottom ?? element.marginBottom ?? 20) : 
                    (localStyles.marginBottom ?? element.marginBottom ?? 20)
                ),
                marginLeft: appendPx(isMobileLayout ? 
                    (element.marginLeftMobile ?? element.marginLeft ?? 0) : 
                    (element.marginLeft ?? 0)
                ),
                marginRight: appendPx(isMobileLayout ? 
                    (element.marginRightMobile ?? element.marginRight ?? 0) : 
                    (element.marginRight ?? 0)
                ),
                position: 'relative',
                cursor: isEditMode ? 'pointer' : 'default'
            }}
            onClick={handleClick}
            onMouseEnter={(e) => {
                if (isEditMode && !isCurrentlyEditing) {
                    e.currentTarget.style.opacity = '0.7';
                }
            }}
            onMouseLeave={(e) => {
                if (isEditMode) {
                    e.currentTarget.style.opacity = '1';
                }
            }}
        >
            {/* ✅ Divider - mit Special Handling für Double Line */}
            {element.dividerStyle === 'double-line' ? (
                <div style={{
                    ...dividerStyles,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: appendPx(localStyles.thickness ?? element.dividerThickness ?? 2),
                    justifyContent: 'center'
                }}>
                    <div style={{
                        height: appendPx(localStyles.thickness ?? element.dividerThickness ?? 2),
                        background: element.dividerColor || colors.accentColor || '#5C80BC',
                        borderRadius: appendPx((localStyles.thickness ?? element.dividerThickness ?? 2) / 2),
                        width: '100%'
                    }} />
                    <div style={{
                        height: appendPx(localStyles.thickness ?? element.dividerThickness ?? 2),
                        background: element.dividerColor || colors.accentColor || '#5C80BC',
                        borderRadius: appendPx((localStyles.thickness ?? element.dividerThickness ?? 2) / 2),
                        width: '100%'
                    }} />
                </div>
            ) : (
                <div style={dividerStyles} />
            )}

            {/* 🎨 Floating Toolbar - Nur wenn aktiv editiert wird */}
            {isEditMode && showToolbar && isCurrentlyEditing && (
                <DividerFloatingToolbar
                    element={element}
                    onClose={handleToolbarClose}
                    onStyleChange={handleStyleChange}
                    colors={colors}
                    anchorRef={dividerRef}
                    isMobileLayout={isMobileLayout}
                />
            )}
        </div>
    );
}

export default UniversalDividerElement;

